import {
    collection,
    onSnapshot,
    updateDoc,
    doc,
    serverTimestamp,
    getDocs,
    getDoc,
    setDoc,
    where,
    query,
    writeBatch,
    increment,
} from "firebase/firestore";
import { db } from "../firebase";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import SessionSummaryModal from "./SessionSummaryModal";
import { THEME_COLOR } from "../config/club";

export default function SessionList({ clubId }) {
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;
    const [openMonths, setOpenMonths] = useState({});

    const toggleMonth = (month) => {
        setOpenMonths(prev => ({
            ...prev,
            [month]: !prev[month]
        }));
    };
    // const migrateStatsToClub = async () => {
    //     try {
    //         const usersSnap = await getDocs(collection(db, "users"));

    //         const batch = writeBatch(db);
    //         let count = 0;

    //         usersSnap.forEach((userDoc) => {
    //             const data = userDoc.data();

    //             if (!data.stats) return;

    //             const { wins = 0, losses = 0, gamesPlayed = 0 } = data.stats;

    //             const clubStatsRef = doc(
    //                 db,
    //                 "users",
    //                 userDoc.id,
    //                 "clubStats",
    //                 clubId
    //             );

    //             batch.set(
    //                 clubStatsRef,
    //                 {
    //                     wins,
    //                     losses,
    //                     gamesPlayed,
    //                     migratedAt: serverTimestamp(),
    //                 },
    //                 { merge: true }
    //             );

    //             count++;
    //         });

    //         await batch.commit();

    //         alert(`Migration completed for ${count} users ✅`);
    //     } catch (err) {
    //         console.error(err);
    //         alert("Migration failed ❌");
    //     }
    // };
    useEffect(() => {
        return onSnapshot(
            collection(db, "clubs", clubId, "sessions"),
            (snap) => {
                setSessions(
                    snap.docs.map((d) => ({ id: d.id, ...d.data() }))
                );
            }
        );
    }, [clubId]);

    const sortedSessions = [...sessions].sort((a, b) => {
        const aSec = a.endedAt?.seconds ?? 0;
        const bSec = b.endedAt?.seconds ?? 0;
        return bSec - aSec; // DESC
    });

    const showSessionQR = (sessionId) => {
        setSelectedSession(null);
        setTimeout(() => {
            const joinUrl = `${process.env.REACT_APP_MAIN_LINK}/join?club=${clubId}&session=${sessionId}`;

            Swal.fire({
                title: "Invite Players",
                padding: '2rem',
                background: 'var(--card-bg)',
                color: 'var(--text-color)',
                html: `
                    <div style="text-align:center">
                        <div style="background: white; padding: 20px; display: inline-block; border-radius: 20px; border: 1px solid #f0f0f0; box-shadow: 0 10px 25px rgba(0,0,0,0.05)">
                             <img src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(joinUrl)}" style="border-radius: 10px;" />
                        </div>
                        <p style="margin-top:20px; font-weight: 600; color: ${THEME_COLOR}">Scan to Join Session</p>
                        <p style="font-size:13px; color:var(--text-muted); max-width: 250px; margin: 0 auto;">
                          Players can scan this code to register for matches automatically.
                        </p>
                    </div>
                `,
                showCloseButton: true,
                showConfirmButton: false,
                customClass: {
                    popup: 'rounded-5 border-0 shadow-lg',
                    closeButton: 'border-0'
                }
            });
        }, 0);
    };

    const endSession = async (sessionId) => {
        const result = await Swal.fire({
            title: "End Session?",
            text: "This will finalize matches and close the queue.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, End Session",
            cancelButtonText: "Wait, Go Back",
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6e7881",
            reverseButtons: true,
            customClass: {
                popup: 'rounded-4'
            }
        });

        if (!result.isConfirmed) return;
        try {
            Swal.fire({
                title: "Finalizing session…",
                text: "Calculating matches and saving stats",
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });
            const sessionRef = doc(db, "clubs", clubId, "sessions", sessionId);
            const sessionSnap = await getDoc(sessionRef);
            const sessionData = sessionSnap.data();

            await updateDoc(sessionRef, {
                status: "ended",
                endedAt: serverTimestamp(),
            });
            // 3️⃣ Load players
            const playersSnap = await getDocs(
                collection(db, "clubs", clubId, "sessions", sessionId, "players")
            );

            const stats = {};
            playersSnap.docs.forEach((d) => {
                stats[d.id] = {
                    playerId: d.id,
                    name: d.data().name,
                    wins: 0,
                    losses: 0,
                    gamesPlayed: 0,
                };
            });

            // 4️⃣ Load matches
            const matchesSnap = await getDocs(
                query(
                    collection(db, "clubs", clubId, "sessions", sessionId, "matches"),
                    where("status", "==", "finished")
                )
            );

            for (const d of matchesSnap.docs) {
                const match = d.data();
                //console.log(match)
                if (
                    match.status !== "finished" ||
                    !match.players ||
                    match.players.length !== 4 ||
                    !match.score
                ) {
                    continue;
                }

                await setDoc(
                    doc(db, "clubs", clubId, "matchHistory", d.id),
                    {
                        sessionId,
                        sessionName: sessionData.name,
                        players: match.players.map(p => ({
                            id: p.id || null,
                            name: p.name
                        })),
                        // 🔥 ADD THIS
                        playerIds: match.players
                            .map(p => p.id)
                            .filter(Boolean),
                        score: match.score,
                        playedAt: match.endedAt,
                    }
                );

                const [scoreA, scoreB] = match.score.split("-").map(Number);
                if (isNaN(scoreA) || isNaN(scoreB)) {
                    continue; // ✅ skip invalid score
                };

                const teamA = match.players.slice(0, 2);
                const teamB = match.players.slice(2, 4);

                const winners = scoreA > scoreB ? teamA : teamB;
                const losers = scoreA > scoreB ? teamB : teamA;

                winners.forEach((p) => {
                    if (stats[p.id]) {
                        stats[p.id].wins += 1;
                        stats[p.id].gamesPlayed += 1;
                    }
                });

                losers.forEach((p) => {
                    if (stats[p.id]) {
                        stats[p.id].losses += 1;
                        stats[p.id].gamesPlayed += 1;
                    }
                });
            };

            const players = Object.values(stats).map((p) => ({
                ...p,
                winRate: p.gamesPlayed
                    ? Math.round((p.wins / p.gamesPlayed) * 100)
                    : 0,
            }));
            // for (const p of players) {
            //     // 🚫 skip guests or missing users
            //     if (!p.playerId) continue;

            //     const userRef = doc(db, "users", p.playerId);
            //     const userSnap = await getDoc(userRef);

            //     const prevStats = userSnap.exists() && userSnap.data().stats
            //         ? userSnap.data().stats
            //         : { gamesPlayed: 0, wins: 0, losses: 0 };

            //     await setDoc(
            //         userRef,
            //         {
            //             stats: {
            //                 gamesPlayed: prevStats.gamesPlayed + p.gamesPlayed,
            //                 wins: prevStats.wins + p.wins,
            //                 losses: prevStats.losses + p.losses,
            //             },
            //             updatedAt: serverTimestamp(),
            //         },
            //         { merge: true }
            //     );
            // }
            const batch = writeBatch(db);

            for (const p of players) {
                if (!p.playerId) continue;

                const clubStatsRef = doc(
                    db,
                    "users",
                    p.playerId,
                    "clubStats",
                    clubId
                );

                batch.set(
                    clubStatsRef,
                    {
                        gamesPlayed: increment(p.gamesPlayed || 0),
                        wins: increment(p.wins || 0),
                        losses: increment(p.losses || 0),
                        updatedAt: serverTimestamp(),
                    },
                    { merge: true }
                );
            }

            await batch.commit();



            // 5️⃣ Duration
            const durationMinutes = sessionData.startedAt
                ? Math.round(
                    (Date.now() - sessionData.startedAt.toDate()) / 60000
                )
                : null;

            // 6️⃣ Save immutable summary
            await setDoc(
                doc(db, "clubs", clubId, "sessions", sessionId, "summary", "final"),
                {
                    sessionName: sessionData.name,
                    totalPlayers: players.length,
                    totalMatches: matchesSnap.size,
                    durationMinutes,
                    players,
                    createdAt: serverTimestamp(),
                }
            );
            // ✅ CLOSE LOADER
            Swal.close();

            // ✅ SUCCESS ALERT
            Swal.fire({
                icon: "success",
                title: "Session Archived",
                text: "All match data has been calculated and saved.",
                customClass: { popup: "rounded-4" }
            });
        } catch (err) {
            console.error("End session failed:", err);

            Swal.close();

            Swal.fire({
                icon: "error",
                title: "Failed to End Session",
                text: "Something went wrong while finalizing the session.",
                customClass: { popup: "rounded-4" }
            });
        }
    };
    const groupSessionsByMonth = (sessions) => {
        const groups = {};

        sessions.forEach((s) => {
            if (!s.createdAt?.seconds) return;

            const date = new Date(s.createdAt.seconds * 1000);

            const key = date.toLocaleString("en-US", {
                month: "long",
                year: "numeric"
            });

            if (!groups[key]) groups[key] = [];
            groups[key].push(s);
        });

        return groups;
    };

    const activeSessions = sortedSessions.filter(s => s.status === "active");
    const endedSessions = sortedSessions.filter(s => s.status !== "active");
    const groupedSessions = groupSessionsByMonth(endedSessions);
    return (
        <div className="mt-2">
            {activeSessions.length > 0 && (
                <div className="mb-4">

                    <h6 className="text-uppercase small fw-bold mb-3 tracking-wider"
                        style={{ color: "#22c55e" }}>
                        Active Session
                    </h6>

                    <div className="d-flex flex-column gap-2">

                        {activeSessions.map((s) => (
                            <div
                                key={s.id}
                                className="list-group-item border rounded-4 d-flex justify-content-between align-items-center py-3 px-4 shadow-sm"
                                style={{
                                    background: `linear-gradient(135deg, var(--card-bg), ${themeAlpha("08")})`,
                                    borderColor: themeAlpha("30")
                                }}
                            >

                                {/* LEFT */}
                                <div className="d-flex align-items-center">
                                    <div
                                        className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: 40,
                                            height: 40,
                                            backgroundColor: themeAlpha("15"),
                                            color: THEME_COLOR
                                        }}
                                    >
                                        <i className="bi bi-play-fill"></i>
                                    </div>

                                    <div>
                                        <div className="fw-bold" style={{ color: "var(--text-color)" }}>
                                            {s.name}
                                        </div>

                                        <span
                                            className="badge rounded-pill"
                                            style={{
                                                backgroundColor: "rgba(34,197,94,0.12)",
                                                color: "#22c55e",
                                                fontSize: "0.65rem"
                                            }}
                                        >
                                            ACTIVE
                                        </span>
                                    </div>
                                </div>

                                {/* ACTIONS */}
                                <div className="d-flex gap-2">

                                    <button
                                        className="btn btn-sm px-3 rounded-pill fw-bold"
                                        style={{
                                            backgroundColor: themeAlpha("15"),
                                            color: THEME_COLOR
                                        }}
                                        onClick={() => showSessionQR(s.id)}
                                    >
                                        <i className="bi bi-qr-code me-1"></i> QR
                                    </button>

                                    <button
                                        className="btn btn-sm px-3 rounded-pill fw-bold text-danger"
                                        style={{
                                            backgroundColor: "rgba(239,68,68,0.15)"
                                        }}
                                        onClick={() => endSession(s.id)}
                                    >
                                        End
                                    </button>

                                </div>
                            </div>
                        ))}

                    </div>

                </div>
            )}
            <h6 className="text-uppercase small fw-bold text-muted mb-3 tracking-wider">Existing Sessions</h6>

            <div className="list-group border-0 gap-2">
                {Object.entries(groupedSessions).map(([month, monthSessions]) => {

                    const isOpen = openMonths[month] ?? true;

                    return (
                        <div key={month} className="mb-3">

                            {/* Month Header */}
                            <div
                                onClick={() => toggleMonth(month)}
                                className="d-flex justify-content-between align-items-center px-4 py-3 rounded-4 month-header mb-2"
                                style={{
                                    cursor: "pointer",
                                    background: "var(--card-bg)",
                                    border: "1px solid var(--border-color)",
                                    transition: "all 0.2s ease"
                                }}
                            >
                                <div className="d-flex align-items-center gap-3">

                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center"
                                        style={{
                                            width: 28,
                                            height: 28,
                                            background: `${THEME_COLOR}20`,
                                            color: THEME_COLOR,
                                            fontWeight: 700
                                        }}
                                    >
                                        {isOpen ? "−" : "+"}
                                    </div>

                                    <div>
                                        <div className="fw-bold" style={{ color: "var(--text-color)" }}>
                                            {month}
                                        </div>
                                        <small className="text-muted">
                                            {monthSessions.length} sessions
                                        </small>
                                    </div>
                                </div>

                                <i
                                    className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
                                    style={{ color: "var(--text-muted)" }}
                                ></i>
                            </div>

                            {/* Session List */}
                            {isOpen && (
                                <div className="d-flex flex-column gap-2">

                                    {monthSessions.map((s) => (
                                        <div
                                            key={s.id}
                                            className="list-group-item list-group-item-action border rounded-4 d-flex justify-content-between align-items-center py-3 px-4 shadow-sm"
                                            style={{
                                                transition: "all 0.2s ease",
                                                background: "var(--card-bg)",
                                                borderColor: "var(--border-color)"
                                            }}
                                        >

                                            {/* LEFT */}
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="rounded-circle me-3 d-none d-md-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: 40,
                                                        height: 40,
                                                        backgroundColor:
                                                            s.status === "active"
                                                                ? themeAlpha("10")
                                                                : "var(--surface-bg)",
                                                        color:
                                                            s.status === "active"
                                                                ? THEME_COLOR
                                                                : "var(--text-muted)"
                                                    }}
                                                >
                                                    <i className={`bi ${s.status === 'active' ? 'bi-play-fill' : 'bi-archive-fill'}`}></i>
                                                </div>

                                                <div>
                                                    <div
                                                        className="fw-bold mb-0"
                                                        style={{ color: "var(--text-color)" }}
                                                    >
                                                        {s.name}
                                                    </div>

                                                    <span
                                                        className="badge rounded-pill fw-medium"
                                                        style={{
                                                            backgroundColor:
                                                                s.status === "active"
                                                                    ? "rgba(34,197,94,0.12)"
                                                                    : "var(--surface-bg)",
                                                            color:
                                                                s.status === "active"
                                                                    ? "#22c55e"
                                                                    : "var(--text-muted)",
                                                            fontSize: "0.65rem"
                                                        }}
                                                    >
                                                        {s.status.toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* RIGHT */}
                                            <div className="d-flex gap-2">

                                                {s.status === "ended" && (
                                                    <button
                                                        className="btn btn-sm px-3 rounded-pill fw-bold border"
                                                        onClick={() => setSelectedSession(s)}
                                                        style={{
                                                            color: "var(--text-color)",
                                                            borderColor: "var(--border-color)"
                                                        }}
                                                    >
                                                        Summary
                                                    </button>
                                                )}

                                                {s.status === "active" && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm px-3 rounded-pill fw-bold"
                                                            style={{
                                                                backgroundColor: themeAlpha("15"),
                                                                color: THEME_COLOR
                                                            }}
                                                            onClick={() => showSessionQR(s.id)}
                                                        >
                                                            <i className="bi bi-qr-code me-1"></i> QR
                                                        </button>

                                                        <button
                                                            className="btn btn-sm px-3 rounded-pill fw-bold text-danger"
                                                            style={{
                                                                backgroundColor: "rgba(239,68,68,0.15)"
                                                            }}
                                                            onClick={() => endSession(s.id)}
                                                        >
                                                            End
                                                        </button>
                                                    </>
                                                )}
                                            </div>

                                        </div>
                                    ))}

                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {selectedSession && (
                <SessionSummaryModal
                    session={selectedSession}
                    onClose={() => setSelectedSession(null)}
                />
            )}
        </div>
    );
}